import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { getCleanedHtml } from "../../../../quill-languagetool";
import { TextvorlageComponent } from "../../../textvorlage/textvorlage.component";
import { buildQuillEditorModules } from "../../quill-editor-config";

type SpeechRecognitionCtor = new () => SpeechRecognition;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  }
}

@Component({
  selector: "app-love-it-textarea-textvorlage",
  templateUrl: "./love-it-textarea-textvorlage.component.html",
  styleUrls: ["./love-it-textarea-textvorlage.component.scss"],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoveItTextareaTextvorlageComponent),
      multi: true,
    },
  ],
})
export class LoveItTextareaTextvorlageComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) formControl!: FormControl;
  @Input({ required: true }) submitted: boolean;
  @Input() textbausteinBezeichnung!: string;
  @Input() label: string;
  @Input() enableFormatting: boolean = false;
  @Output() onTextEdited: EventEmitter<string> = new EventEmitter<string>();

  quillEditorModules: any;

  value: string = "";
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  listening = false;
  private recognition: SpeechRecognition | null = null;

  constructor(
    private modalController: ModalController,
    private oidcSecurityService: OidcSecurityService,
  ) {}

  ngOnInit() {
    this.quillEditorModules = buildQuillEditorModules(this.oidcSecurityService.getToken(), this.enableFormatting);

    if (!this.enableFormatting && this.formControl.value) {
      const plainText = this.stripHtml(this.formControl.value);
      if (plainText !== this.formControl.value) {
        this.formControl.setValue(plainText, { emitEvent: false });
      }
    }
  }

  get speechToTextAvailable(): boolean {
    return typeof window !== "undefined" && (!!window.SpeechRecognition || !!window.webkitSpeechRecognition);
  }

  toggleSpeechToText(): void {
    if (!this.formControl || this.formControl.disabled || !this.speechToTextAvailable) {
      return;
    }

    if (this.listening) {
      this.recognition?.stop();
      return;
    }

    const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!Ctor) {
      return;
    }

    const recognition = new Ctor();
    recognition.lang = "de-AT";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      this.listening = true;
    };

    recognition.onend = () => {
      this.listening = false;
      this.recognition = null;
    };

    recognition.onerror = () => {
      this.listening = false;
      this.recognition = null;
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const results = Array.from(event.results).slice(event.resultIndex);

      const finalText = results
        .filter((r) => r.isFinal)
        .map((r) => r[0].transcript)
        .join(" ")
        .trim();

      if (!finalText) {
        return;
      }

      const current = String(this.formControl.value ?? "");
      const next = current.trim().length === 0 ? finalText : `${current.trimEnd()} ${finalText}`;

      if (next !== current) {
        this.formControl.setValue(next);
        this.formControl.markAsDirty();
        this.formControl.markAsTouched();
        this.onTextEdited.emit(next);
      }
    };

    this.recognition = recognition;
    recognition.start();
  }

  writeValue(value: string): void {
    this.value = value || "";
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  async openTextbausteinModal(formControl: FormControl, field: string) {
    const modal = await this.modalController.create({
      component: TextvorlageComponent,
      cssClass: ["resizeable-modal", "textvorlage-modal-size"],
      backdropDismiss: false,
      componentProps: {
        content: formControl?.value,
        field,
        enableFormatting: this.enableFormatting,
      },
    });

    modal.onDidDismiss().then((data) => {
      const d = data.data;
      if (d && d.keep) {
        this.setFormControlText(formControl, d.content);
      }
    });

    await modal.present();
  }

  setFormControlText(formControl: FormControl, newValue: any) {
    const value = !this.enableFormatting && newValue ? this.stripHtml(newValue) : newValue;
    this.onTextEdited.emit(value);
    formControl.patchValue(value);
    formControl.markAsDirty();
  }

  onQuillContentChanged(event: any) {
    const rawValue = this.formControl.value ?? "";
    this.onTextEdited.emit(this.enableFormatting ? getCleanedHtml(rawValue) : rawValue);
  }

  private stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }

  protected readonly Validators = Validators;
}
